import type { CSSProperties, ReactNode } from 'react';
import { cn } from '../../utils/cn';

// =============================================================================
// Shared skeleton primitives for NBL admin loading states.
//
// Every block uses the .skeleton-shimmer utility defined in globals.css, which
// switches between light/dark palettes automatically via CSS variables.
//
// Layout skeletons (see ./layouts/) compose these primitives to mirror the
// structure of each page while initial data is still loading.
// =============================================================================

type BaseProps = {
  className?: string;
  style?: CSSProperties;
};

export function Skeleton({ className, style }: BaseProps): JSX.Element {
  return <div className={cn('skeleton-shimmer', className)} style={style} />;
}

export function SkeletonText({
  width,
  height = '1em',
  className,
}: BaseProps & { width?: string | number | undefined; height?: string | number | undefined }): JSX.Element {
  return <Skeleton className={cn('inline-block', className)} style={{ width, height }} />;
}

export function SkeletonRect({
  width,
  height,
  className,
}: BaseProps & { width?: string | number | undefined; height?: string | number | undefined }): JSX.Element {
  return <Skeleton className={cn('block', className)} style={{ width, height }} />;
}

export function SkeletonCircle({
  size = 24,
  className,
}: BaseProps & { size?: number }): JSX.Element {
  return (
    <Skeleton
      className={cn('rounded-full', className)}
      style={{ width: size, height: size }}
    />
  );
}

export function SkeletonButton({
  width = 80,
  height = 30,
  className,
}: BaseProps & { width?: number | undefined; height?: number | undefined }): JSX.Element {
  return <Skeleton className={cn('block', className)} style={{ width, height }} />;
}

export function SkeletonInput({
  width = 160,
  height = 38,
  className,
}: BaseProps & { width?: number | undefined; height?: number | undefined }): JSX.Element {
  return <Skeleton className={cn('block', className)} style={{ width, height }} />;
}

/** A horizontal row of skeleton text lines with consistent gaps. */
export function SkeletonTextLines({
  lines = 3,
  widths = ['100%', '85%', '60%'],
  height = '1em',
  gap = 8,
  className,
}: {
  lines?: number;
  widths?: string[];
  height?: string | number | undefined;
  gap?: number;
  className?: string;
}): JSX.Element {
  return (
    <div className={cn('flex flex-col', className)} style={{ gap }}>
      {Array.from({ length: lines }).map((_, i) => (
        <SkeletonText key={i} width={widths[i % widths.length]} height={height} />
      ))}
    </div>
  );
}

/** A bordered panel-like block (no internal padding by default). */
export function SkeletonPanel({
  children,
  className,
  header = true,
  title,
  trailing,
}: {
  children: ReactNode;
  className?: string;
  header?: boolean;
  title?: ReactNode;
  trailing?: ReactNode;
}): JSX.Element {
  return (
    <div
      className={cn(
        'rounded border border-line bg-panel overflow-hidden',
        className,
      )}
    >
      {header && (
        <div className="flex items-center justify-between border-b border-line px-4 py-3">
          {title ?? <SkeletonText width={120} height="1em" />}
          {trailing ?? <SkeletonButton width={70} height={26} />}
        </div>
      )}
      {children}
    </div>
  );
}

/** A table row composed of skeleton cells. */
export function SkeletonTableRow({
  cells,
  className,
}: {
  cells: Array<{ width?: string | number | undefined; align?: 'left' | 'right' | 'center' | undefined }>;
  className?: string;
}): JSX.Element {
  return (
    <tr className={cn('border-b border-line', className)}>
      {cells.map((cell, i) => (
        <td key={i} className="px-4 py-3" style={{ textAlign: cell.align ?? 'left' }}>
          <SkeletonText width={cell.width ?? '80%'} />
        </td>
      ))}
    </tr>
  );
}

/** A complete skeleton table with a header row and N body rows. */
export function SkeletonTable({
  columns,
  rowCount = 5,
  className,
}: {
  columns: Array<{ label?: string | undefined; width?: string | number | undefined; align?: 'left' | 'right' | 'center' | undefined }>;
  rowCount?: number;
  className?: string;
}): JSX.Element {
  return (
    <table data-mobile-table="keep" className={cn('w-full border-collapse text-[14px]', className)}>
      <thead>
        <tr className="border-b border-line bg-panel-2 text-left text-text-dim">
          {columns.map((col, i) => (
            <th key={i} className="px-4 py-3 font-medium" style={{ width: col.width, textAlign: col.align ?? 'left' }}>
              {col.label ?? <SkeletonText width={col.width ?? '60%'} />}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {Array.from({ length: rowCount }).map((_, r) => (
          <SkeletonTableRow key={r} cells={columns.map((c) => ({ width: c.width, align: c.align }))} />
        ))}
      </tbody>
    </table>
  );
}

/** Standard page header skeleton: breadcrumb line + title. */
export function SkeletonPageHeader({ className }: { className?: string }): JSX.Element {
  return (
    <header className={cn('mb-4 flex flex-col items-start', className)}>
      <SkeletonText width={180} height="0.85em" className="mb-1" />
      <SkeletonText width={220} height="1.4em" />
    </header>
  );
}
